/**
 * Safely convert any value (usually from an API response) into a displayable
 * string. Backends sometimes return messages as a string OR as a nested object
 * like `{ message: "..." }` / `{ error: { message: "..." } }`. Storing such an
 * object in React state and rendering it throws:
 *   "Objects are not valid as a React child (found: object with keys {message})"
 *
 * This normalizes those shapes so rendering never receives an object.
 *
 * @param {*} value  The payload to convert.
 * @param {string} [fallback]  Value to return if nothing sensible is found.
 * @returns {string}
 */
export function toDisplayString(value, fallback = "") {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const parts = value.map((entry) => toDisplayString(entry, "")).filter(Boolean);
    return parts.length ? parts.join(", ") : fallback;
  }
  if (typeof value === "object") {
    const candidate =
      value.message ?? value.error ?? value.description ?? value.detail ?? value[0];
    if (candidate !== undefined && candidate !== null) {
      return toDisplayString(candidate, fallback);
    }
    // Validation error map: { field: "msg" } / { field: ["msg"] }
    const parts = Object.entries(value)
      .map(([field, entry]) => {
        const message = toDisplayString(entry, "");
        return message ? `${field}: ${message}` : "";
      })
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
    return fallback || JSON.stringify(value);
  }
  return fallback;
}

/**
 * Extract a user-facing message from anything an API call can throw: a plain
 * Error, an axios-style error (`error.response.data`), or a raw API payload.
 * Always prefers the backend's own message over the fallback.
 */
export function getErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  if (error === undefined || error === null) return fallback;
  if (typeof error === "string") return error || fallback;
  if (error.response) return toDisplayString(error.response.data, fallback); // axios
  if (error.message) {
    const message = toDisplayString(error.message, "");
    if (message) return message;
  }
  return toDisplayString(error, fallback);
}

/**
 * Strip HTML markup down to readable plain text.
 *
 * Rich-text fields (e.g. the course "Description") are stored as HTML so the
 * author's formatting survives. Anywhere that text is rendered inside a plain
 * element — `<span>{course.description}</span>` — the raw tags would otherwise
 * be shown literally to the reader, so run it through this first.
 *
 * Safe by construction: the result is inserted as a React text child (never
 * via dangerouslySetInnerHTML), so no markup is ever executed.
 */
export function htmlToText(value, fallback = "") {
  if (value === undefined || value === null) return fallback;
  const raw = String(value);
  if (!raw) return fallback;
  // Turn block boundaries into line breaks so paragraphs do not run together.
  const withBreaks = raw
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\/\s*(p|div|h[1-6]|li|blockquote|tr)\s*>/gi, "\n")
    .replace(/<\s*li[^>]*>/gi, "• ");
  const text = withBreaks
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return text || fallback;
}

/**
 * Translate a backend error into something a non-technical person can act on.
 *
 * Two problems this solves:
 *  1. `toDisplayString` already prevents "[object Object]", but the backend's
 *     own text is often developer-facing ("Only draft courses can be edited",
 *     "409", "Validation failed for field moduleId"). Those leak jargon and
 *     tell the author nothing about what to do next.
 *  2. A bare HTTP status tells the user nothing when no message came back.
 *
 * So: detect known backend phrasings and map them to plain instructions, then
 * fall back to the backend's message, then to a per-status default.
 *
 * @param {number} status  HTTP status code (0/undefined when there was no response).
 * @param {*} payload      Parsed response body (may be `{error:{message}}`, a
 *                         validation map, or a plain string).
 * @param {string} [fallback]  Message to use when nothing else is available.
 * @returns {string} A sentence aimed at the author, not at the API.
 */
export function explainApiError(status, payload, fallback = "") {
  const raw = toDisplayString(payload?.error ?? payload?.message ?? payload?.errors ?? payload, "");
  // toDisplayString serialises an unrecognised object with JSON.stringify, so an
  // empty body arrives here as the literal text "{}" — which would then be shown
  // to the author as if it were the real message. Treat those as "no message".
  const EMPTYISH = new Set(["", "{}", "[]", "null", "undefined", "0"]);
  const text = EMPTYISH.has(String(raw ?? "").trim()) ? "" : String(raw).trim();

  // Statuses whose MEANING is decided by the transport, not by the body. A proxy
  // answers 413 with an HTML error page, so `text` is "<html>Request Entity Too
  // Large</html>" and the author would be shown raw markup instead of a sentence.
  // These win over the body.
  if (status === 413) return "The upload service rejected this file as too large. Try a smaller or compressed image.";
  if (status === 429) return "Too many requests. Please wait a moment and try again.";
  if (status >= 500 && !text) return "Something went wrong on our end. Please try again in a moment.";

  // Known backend phrasings -> concrete next step for the author.
  const known = [
    {
      test: /cannot be edited|can'?t be edited|can only be edited|not editable|only\s+\w+[\w\s]*courses?\s+can\s+be\s+edited/i,
      message: "This course can't be edited right now because it has already been submitted for review. Withdraw it first if you need to make changes.",
    },
    {
      test: /only .*pending.* (can|could) be unsubmitted|can only be withdrawn|not in pending status|must be pending/i,
      message: "Only a course that's waiting for review can be withdrawn.",
    },
    {
      test: /module is not empty|module.*(contains|has) lessons|delete.*empty module/i,
      message: "This module still has lessons in it. Move or delete those lessons first, then delete the module.",
    },
    {
      test: /at least one lesson|requires? (a|at least one) lesson|lesson.*required.*submit/i,
      message: "Add at least one lesson before submitting this course for review.",
    },
    {
      test: /validation failed|is invalid|is required|must be (a|an|one of)|failed to validate/i,
      message: "Some of the details you entered need fixing. Check the highlighted fields and try again.",
    },
    {
      test: /duplicate|already exists|unique constraint/i,
      message: "Something with that name already exists. Try a different name.",
    },
    {
      test: /failed to fetch|network\s*error|networkerror|load failed|err_failed|failed to load|fetch failed/i,
      message: NETWORK_ERROR_MESSAGE,
    },
    {
      test: /jwt expired|jwt|token expired|invalid token|unauthorized/i,
      message: "Your session has expired. Please sign in again and retry.",
    },
  ];
  for (const rule of known) {
    if (text && rule.test.test(text)) return rule.message;
  }

  if (text) return text;

  if (status === 400 || status === 422) return "Some of the details you entered aren't valid. Please check and try again.";
  if (status === 401) return "Your session has expired. Please sign in again and retry.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "We couldn't find what you were looking for. It may have been deleted.";
  if (status === 409) return "That conflicts with the current state of this item. Refresh the page and try again.";
  if (status === 413) return "The upload service rejected this file as too large. Try a smaller or compressed image.";
  if (status === 429) return "Too many requests. Please wait a moment and try again.";
  if (status >= 500) return "Something went wrong on our end. Please try again in a moment.";
  if (!status) return NETWORK_ERROR_MESSAGE;
  return fallback || "That didn't work. Please try again.";
}

/** Message shown when a request never reached the server. */
export const NETWORK_ERROR_MESSAGE =
  "We couldn't connect right now. Check your internet connection and try again.";

/**
 * `fetch` wrapper that translates a network-level failure into a readable Error.
 *
 * The browser rejects with a bare `TypeError: Failed to fetch` when the request
 * never reaches the server (offline, DNS failure, CORS rejection, blocked or
 * aborted request). That text means nothing to an author, so it is normalised
 * once here rather than at every call site.
 *
 * HTTP error responses are deliberately untouched — those still travel through
 * `explainApiError`, which has the per-status wording.
 */
export async function safeFetch(url, options) {
  try {
    return await fetch(url, options);
  } catch (error) {
    const wrapped = new Error(NETWORK_ERROR_MESSAGE);
    wrapped.isNetworkError = true;
    wrapped.cause = error;
    throw wrapped;
  }
}

export default toDisplayString;
